/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ICON_VARIANT_LEFT } from '@aglyn/shared-data-enums'
import { AppLink, type AppLinkProps, MdiIcon, type MdiIconProps, Menu, type MenuItemProps, type MenuProps, SrOnly } from '@aglyn/shared-ui-jsx'
import { ScrollReaction } from '@aglyn/shared-ui-jsx/components/scroll-reaction'
// Subpath, not the barrel: the barrel reaches `svg-icons.tsx`, which draws
// five more wordmarks the console never renders. The besigner's wordmark is
// not here either — it arrives as `wordmark` from the editor shells that draw
// it, so a page carries only the mark it renders.
import { AglynConsoleLogoFull } from '@aglyn/shared-ui-jsx/const/aglyn-console-logo-full'
import { useIsNativeAppWebView } from '@aglyn/aglyn/app-utils/native-app-web-view'
import { mergeSxProps } from '@aglyn/shared-ui-theme'
import { _isArrEmpty } from '@aglyn/shared-util-tools'
import {
  AppBar,
  Avatar,
  type AvatarProps,
  Box,
  Button,
  type ButtonProps,
  Divider,
  IconButton,
  type IconButtonProps,
  Stack,
  type StackProps,
  Toolbar,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material'
import { Fragment } from 'react'
import AppBarMenubarComponent from './app-bar-menubar.component'
import GlobalSearchTriggerComponent from '../global-search/global-search-trigger.component'
import { buildRoute, Route } from '../../constants/route-links'
import useBranding from '../../hooks/use-branding'
import { onlyInScheme } from '../../utils/scheme-visibility'
import { useOrgSlug } from '../../hooks/use-org-scope'
import { useUrlNamesOrg } from '../../hooks/use-secondary-nav'
import { TOP_BAR_HEIGHT } from '../../constants/shared'
import NotificationPrompt from '../notification-prompt.component'
import NotificationsMenu from '../notifications-menu.component'
import OfflineIndicator from '../offline-indicator.component'
import ConsoleTopBarSlot from '../console-top-bar-slot.component'
import OrgSwitcherNav from '../org-switcher-nav.component'
import { PinnedOrgsKeepPrompt } from '../pinned-orgs-dialogs.component'
import UserMenu from '../user-menu.component'

// Icon-only quick actions. The center nav used to come through here too, as a
// `type: 'text'` branch; it is a Base UI menubar now (AGL-1222), so this is
// down to the one shape it still serves.
const buildNav = () => (item, i) => {
  // Escape hatch for a fully custom action (AGL-858: the redesigned UserMenu),
  // rendered in place instead of squeezed through the icon/menu item shape.
  if (typeof item?.render === 'function') {
    return item.render(item.key ?? item.id ?? i)
  }
  const { avatar, icon, children, id, key, items, MenuProps, ...rest } = item
  const isMenu = !_isArrEmpty(items)
  const itemKey = key || id || i

  /**
   * An icon-only button with no accessible name is announced as "button"
   * and nothing else (AGL-2128). Everything here is icon-only by
   * construction, and the name arrives — if it arrives — through `...rest`
   * from whatever page supplied the action, so nothing in this file could
   * guarantee one. Derive a last resort from the item's own identity rather
   * than shipping an anonymous control: an explicit `aria-label`, `title` or
   * `aria-labelledby` from the caller still wins, because `...rest` spreads
   * after this.
   */
  const fallbackLabel =
    rest['aria-label'] || rest.title || rest['aria-labelledby']
      ? undefined
      : String(item?.label ?? id ?? key ?? '') || undefined

  const rendered = (
    <IconButton
      key={itemKey}
      color="inherit"
      {...(fallbackLabel ? { 'aria-label': fallbackLabel } : {})}
      // AGL-752: an `href` or a custom `component` resolves this to an <a>.
      {...(rest.href || rest.component ? { nativeButton: false } : {})}
      {...rest}
    >
      {!avatar ? (
        !icon?.path ? (
          icon
        ) : (
          <MdiIcon {...icon} />
        )
      ) : (
        <Avatar {...avatar} />
      )}
      {children}
    </IconButton>
  )

  return isMenu ? (
    <Menu
      dense
      key={itemKey}
      items={items}
      {...MenuProps}
      sx={mergeSxProps(
        {
          p: { padding: 0.5, xs: 0.25 },
        },
        MenuProps?.sx,
      )}
    >
      {rendered}
    </Menu>
  ) : (
    <Fragment key={itemKey}>{rendered}</Fragment>
  )
}

export interface TopAppBarProps {
  appBarSuffix?: JSX.Node
  centerNavigationItems?: CenterNavMenuItem[]
  /** Rendered before the center nav items (e.g. the version dropdown). */
  centerPrefix?: JSX.Node
  /** Rendered on the right, before the quick actions / user menu (AGL-57). */
  actionsPrefix?: JSX.Node
  customCenter?: JSX.Node
  enableAppBarElevation?: boolean
  quickActions?: QuickActionsMenuItem[]
  /**
   * The product wordmark worn by the app bar, defaulting to the console's.
   *
   * A slot rather than a second branch of the `besigner` flag because each
   * wordmark is ~13 KB of outlined path data: branching here put both marks
   * in the shell every route loads, so every console page carried the
   * besigner logotype it never draws. The shell that knows which product it
   * is supplies the mark, and a route ships one.
   *
   * White-label chrome still wins over it: an entitled org's own logo or
   * product name replaces whatever wordmark the shell passed.
   */
  wordmark?: JSX.Node
  backButton?: Partial<ButtonProps>
  /**
   * The editor's bar on a screen too narrow for it: the wordmark, search
   * and account cluster step aside and the center menubar folds into one
   * menu button, so the back arrow, the document and the publish actions
   * keep their room. The back arrow still leaves the editor.
   */
  compact?: boolean
}

const TopAppBar = (props: TopAppBarProps) => {
  const {
    appBarSuffix,
    centerNavigationItems,
    centerPrefix,
    actionsPrefix,
    customCenter,
    enableAppBarElevation,
    quickActions,
    wordmark,
    backButton,
    compact = false,
  } = props
  // The logo returns to the active org's home (AGL-631); the jump page when no
  // org has resolved yet — or when the URL names no workspace at all
  // (AGL-1130), where "the active org" is only whichever one the scope fell
  // back to. From the staff console or Manage Account, home is the picker.
  const resolvedOrgSlug = useOrgSlug()
  const orgSlug = useUrlNamesOrg() ? resolvedOrgSlug : ''
  const orgHome = orgSlug ? buildRoute(Route.ORG_HOME, { orgSlug }) : '/'
  // White-label chrome (White-Label Phase 2): swap the Aglyn wordmark for the
  // current org's brand when it is white-label entitled.
  const { branding, whiteLabel } = useBranding()
  // Inside the native apps the editor is a screen of the app, and the app's
  // own Done button is the way out (AGL-3894). The web arrow there led to the
  // console page the app hands straight back, a second control for the same
  // exit.
  const inNativeApp = useIsNativeAppWebView()
  const showBackButton = Boolean(backButton) && !inNativeApp

  return (
    <ScrollReaction>
      {({ activeWithoutHysteresis }) => (
        <AppBar
          component="header"
          color="surface"
          variant="elevation"
          elevation={enableAppBarElevation && activeWithoutHysteresis ? 4 : 0}
          // Always scrolls away (user request 2026-07-07): the secondary
          // nav-tabs bar is the sticky one; pages that passed
          // enableAppBarElevation used to pin this bar too, so which bar
          // stuck varied page to page.
          position="relative"
          sx={{
            height: `${TOP_BAR_HEIGHT - 1}px`,
            borderBottomWidth: `1px`,
            borderBottomStyle: 'solid',
            borderBottomColor: 'divider',
            zIndex: (theme) => theme.zIndex.appBar + 5,
          }}
        >
          <Toolbar
            component={Stack}
            variant="dense"
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "flex-start",
              // `sx` was a typo for the `xs` breakpoint (AGL-1414), so the
              // phone gutter silently fell through to Toolbar's 16px default
              // and the narrowest viewport got the widest padding.
              paddingLeft: { xs: 1, sm: 2 },
            }}
            divider={
              <Divider orientation="vertical" variant="middle" flexItem sx={{ opacity: 0.5 }} />
            }
          >
            {backButton && !inNativeApp && (
              <Button
                // AGL-752: every caller passes `component: AppLink`, which
                // resolves to an <a>; declare it so MUI v7's button-root check
                // stays quiet. Overridable by the caller via the spread below.
                {...(backButton.component ? { nativeButton: false } : {})}
                {...backButton}
                sx={{
                  minWidth: 'unset',
                  // position: 'absolute',
                  // Cancels the Toolbar's left gutter so the arrow sits on it rather
                  // than inset by it — the gutter is 8px on a phone, 16px above.
                  marginLeft: { xs: -1, sm: -2 },
                  paddingRight: { xs: 1, sm: 0.75 },
                  paddingLeft: { xs: 0.5, sm: 0.25 },
                  py: { xs: 0, sm: 0 },
                  left: 0,
                  height: 1,
                  borderRadius: 0,
                  // borderRight: ({ palette }) => `1px solid ${palette.divider}`,
                }}
              >
                <MdiIcon
                  sx={{ fontSize: '1.2em' }}
                  path={ICON_VARIANT_LEFT.path}
                />
                <SrOnly>{'back'}</SrOnly>
              </Button>
            )}

            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "flex-start",
                color: "inherit",
                maxWidth: { xs: '100%' },
                paddingLeft: showBackButton ? 0.5 : undefined,
                ...(compact && { display: 'none' }),

                // The wordmark is a fixed-size brand mark, so it is never what
                // gives when the bar runs out of room (AGL-1414) — the org
                // switcher beside it is. Without this the logo compressed
                // instead of the switcher yielding.
                flexShrink: 0,

                // width: DRAWER_WIDTH - 26,
                paddingRight: { xs: 1, sm: 3 }
              }}>
              <AppLink
                href={orgHome}
                componentVariant="button-base"
                color="inherit"
                disableRipple
              >
                <Stack
                  component="span"
                  direction="row"
                  spacing={0.25}
                  sx={{
                    alignItems: "center",
                    justifyContent: "flex-start",
                    fontWeight: 'fontWeightRegular',
                    fontFamily: 'h6.fontFamily',

                    fontSize: (theme) => ({
                      fontSize: theme.typography.pxToRem(18),
                      md: theme.typography.pxToRem(20),
                    })
                  }}>
                  {/* White-label console chrome (White-Label Phase 2): a
                      white-label-entitled org shows its own logo (or product
                      name when it set a name but no logo) in place of the
                      Aglyn wordmark, resolved through the one shared
                      `resolveBrandingProfile` (via useBranding). Non-white-label
                      orgs — and every surface until the org doc is confirmed —
                      keep the wordmark the shell passed. */}
                  {whiteLabel && branding.logoUrl ? (
                    <>
                      <Box
                        component="img"
                        src={branding.logoUrl}
                        alt={branding.productName}
                        sx={[
                          { height: 24, width: 'auto' },
                          branding.logoDarkUrl
                            ? onlyInScheme('light')
                            : { display: 'block' },
                        ]}
                      />
                      {/* The dark-ground variant, when the brand set one:
                          both are in the markup and the scheme class picks,
                          so a dark console never flashes the light mark. */}
                      {branding.logoDarkUrl ? (
                        <Box
                          component="img"
                          src={branding.logoDarkUrl}
                          alt={branding.productName}
                          sx={[
                            { height: 24, width: 'auto' },
                            onlyInScheme('dark'),
                          ]}
                        />
                      ) : null}
                    </>
                  ) : whiteLabel ? (
                    <Typography
                      component="span"
                      color="textPrimary"
                      sx={{
                        fontWeight: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {branding.productName}
                    </Typography>
                  ) : (
                    (wordmark ?? (
                      <AglynConsoleLogoFull sx={{ height: 24, width: 'auto' }} />
                    ))
                  )}
                  {appBarSuffix && (
                    <Typography
                      component="span"
                      color="textPrimary"
                      sx={{
                        fontWeight: "inherit",
                        fontSize: "inherit",
                        lineHeight: "inherit",
                        display: "flex",
                        alignItems: "center"
                      }}>
                      {appBarSuffix}
                    </Typography>
                  )}
                </Stack>
              </AppLink>
            </Stack>

            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "flex-start",
                flexGrow: 1,
                // THE fix for AGL-1414. A flex child defaults to
                // `min-width: auto`, i.e. "never shrink below my content", so
                // a long org name grew this column without bound and shoved
                // the notifications bell and the avatar off the right edge —
                // and every `text-overflow: ellipsis` further down silently
                // did nothing, because nothing ever constrained the box.
                minWidth: 0,
                paddingLeft: { xs: 0.5, sm: 1.5 },
                // A squeezed center truncates its document name instead of
                // painting over the actions beside it. Its menus portal out,
                // so nothing they open is clipped.
                ...(compact && { overflow: 'hidden' }),
              }}>
              {!customCenter &&
              !centerPrefix &&
              _isArrEmpty(centerNavigationItems) ? null : (
                <Stack
                  component="nav"
                  direction="row"
                  sx={{
                    alignItems: "center",
                    justifyContent: "flex-start",
                    // The min-width:0 chain has to reach the label itself;
                    // one `auto` anywhere between re-establishes the floor.
                    minWidth: 0
                  }}>
                  {centerPrefix}
                  {/* The center nav is the besigner's FILE / EDIT / INSERT
                      bar, and it is a real menubar now (AGL-1222) — roving
                      focus across the triggers, hover-to-switch, typeahead
                      that crosses menus. The item data is unchanged; only the
                      mechanics moved. Quick actions keep the plain `Menu`. */}
                  {customCenter || (
                    <AppBarMenubarComponent
                      entries={centerNavigationItems}
                      compact={compact}
                    />
                  )}
                </Stack>
              )}
            </Stack>
            {/*
              Console-wide search (AGL-2179). Deliberately an icon in this
              `flexShrink: 0` cluster rather than a field in the centre slot:
              that slot carries the besigner menubar and the `min-width: 0`
              chain AGL-1414 established, and a text field spliced into it is
              the one change no unit test can verify. The button takes a fixed
              ~40px from the `flexGrow: 1` column instead, and renders nothing
              at all where there is nothing to search.
            */}
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              {compact ? null : <GlobalSearchTriggerComponent />}
            </Stack>
            {actionsPrefix ? (
              <Stack
                component="nav"
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "flex-end",
                  // The icon cluster is the thing that must NEVER move or
                  // compress (AGL-1414): it is anchored to the right edge and
                  // its targets are already at the minimum tap size.
                  flexShrink: 0
                }}>
                {actionsPrefix}
              </Stack>
            ) : null}
            {compact || _isArrEmpty(quickActions) ? null : (
              <Stack
                component="nav"
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "flex-start",
                  // Same contract as the actionsPrefix cluster above — the
                  // avatar keeps its size and its place (AGL-1414).
                  flexShrink: 0
                }}>
                {(quickActions ?? []).map(buildNav())}
              </Stack>
            )}
          </Toolbar>
        </AppBar>
      )}
    </ScrollReaction>
  );
}
TopAppBar.displayName = 'TopAppBar'

export interface QuickActionsMenuItem extends IconButtonProps {
  icon?: MdiIconProps
  avatar?: AvatarProps
  dense?: boolean
  href?: any
  items?: MenuItemProps[]
  MenuProps?: Partial<MenuProps>
  /** Render a fully custom action in place (AGL-858: the UserMenu popover). */
  render?: (key: string | number) => JSX.Element
}

export interface CenterNavMenuItem
  extends Omit<AppLinkProps<'button'>, 'componentVariant'> {
  icon?: MdiIconProps
  avatar?: any
  dense?: boolean
  href?: any
  MenuProps?: Partial<MenuProps>
  items?: MenuItemProps[]
}

// `title` stays omitted from StackProps so no stray HTML title attribute
// reaches the Stack. MainLayout took a `title` prop of its own until AGL-1059
// moved document titles to App Router `metadata`; nothing set the visible
// chrome from it, so the prop went with the title mechanism.
export interface MainLayoutProps
  extends Omit<StackProps, 'title'>,
    TopAppBarProps {
  children?: JSX.Children
  /**
   * Lay the shell out as the besigner's fixed editor: exactly the window
   * height, never page-scrollable.
   *
   * A layout flag, not a branding one — the app bar takes its mark through
   * `wordmark` and reads this nowhere.
   */
  besigner?: boolean
}

export function MainLayout(props: MainLayoutProps) {
  const {
    children,
    appBarSuffix,
    centerNavigationItems,
    centerPrefix,
    actionsPrefix,
    customCenter,
    enableAppBarElevation,
    quickActions,
    besigner,
    wordmark,
    backButton,
    ...rest
  } = props
  // The account menu, theme control, and org-scoped chrome links moved into
  // the self-contained UserMenu component (AGL-858), which reads its own
  // user/org/theme — so MainLayout no longer needs them here.
  const theme = useTheme()
  const narrow = useMediaQuery(theme.breakpoints.down('md'))
  // Only the editor folds its bar: a console page's bar already fits a phone.
  const compact = Boolean(besigner) && narrow
  // The native apps keep their own notifications (push, an inbox screen), so
  // the web bell and its unread count stay out of their web view (AGL-3894).
  const inNativeApp = useIsNativeAppWebView()
  return (
    <Stack
      {...rest}
      sx={[{
        alignItems: "stretch",
        flexDirection: "column",
        // minHeight, not height: a fixed 100vh box is the containing block
        // for the sticky secondary toolbar, which stopped sticking after
        // one viewport of scroll on longer pages (AGL-37).
        minHeight: "100vh",
        // Besigner is a fixed editor shell (AGL-58/63): exactly the window
        // height, never page-scrollable — overflow lives inside the editor
        // regions (canvas pan, panel scroll).
        ...(besigner && {
          height: "100vh",
          overflow: "hidden",
          '@supports (height: 100dvh)': {
            minHeight: "100dvh",
            height: "100dvh",
          },
        }),
      }, ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx])]}>
      <TopAppBar
        enableAppBarElevation={enableAppBarElevation}
        wordmark={wordmark}
        backButton={backButton}
        compact={compact}
        centerPrefix={centerPrefix}
        centerNavigationItems={centerNavigationItems || []}
        customCenter={
          // Default center nav is the ORG switcher (AGL-236 — swapped
          // with the host switcher, which now lives in the secondary
          // bar); pages passing their own nav items or center keep them.
          customCenter ??
          (centerNavigationItems ? undefined : <OrgSwitcherNav />)
        }
        actionsPrefix={
          // Notifications bell (AGL-260) rides beside any page-provided
          // prefix actions.
          <>
            {actionsPrefix}
            {/* Connection state (AGL-1056). HERE because this bar is the
                console's one piece of genuinely persistent chrome — mounted
                by the `(app)` layout, by the besigner and by the screen view
                page, so a single mount covers every authoring surface, and it
                survives navigation rather than blinking on each one. Silent
                on a healthy connection. */}
            <OfflineIndicator />
            {/* A plugin's indicator (AGL-3593) — work in progress or waiting
                on the reader — beside the bell, on the same persistent
                chrome. Draws nothing when no plugin has anything to say. */}
            <ConsoleTopBarSlot />
            {compact || inNativeApp ? null : <NotificationsMenu />}
            {/* Pre-permission ask (AGL-663): the browser allows exactly one
                native prompt per origin, so we offer in-app first where a
                decline is reversible. Renders nothing unless it applies. */}
            <NotificationPrompt />
            {/* "Keep which 5?" (AGL-3943): asked once of a reader who pinned
                more than five workspaces before pins were capped. Renders
                nothing for everyone else; not over the Besigner. */}
            {besigner || inNativeApp ? null : <PinnedOrgsKeepPrompt />}
          </>
        }
        appBarSuffix={appBarSuffix}
        quickActions={[
          ...(quickActions || []),
          // The redesigned account menu (AGL-858) owns its own avatar trigger
          // + popover, and the theme control now lives inside it — so it
          // renders as a custom node rather than an icon/menu item.
          { render: (key: string | number) => <UserMenu key={key} /> },
        ]}
      />
      {besigner ? (
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      ) : (
        children
      )}
    </Stack>
  );
}

MainLayout.displayName = 'MainLayout'
MainLayout.aglyn = true

export default MainLayout
